import classNames from 'classnames'
import { FormikErrors, FormikProps, useFormikContext, withFormik } from 'formik'
import { t } from 'i18next'
import React, { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useResolvedAppTestProduct } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useResolvedAppTestProduct'

import { useSessionEntity } from '@context/EntityContext'
import { usePublicDomainsReadContext } from '@context/PublicDomainsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Heading6 } from '@ui/Typography'
import { AlertNotice } from '@ui/components/AlertNotice'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { ChatCircleDotsIcon } from '@ui/icons/ChatCircleDots.icon'
import { MailIcon } from '@ui/icons/Mail.icon'
import { PhoneIcon } from '@ui/icons/Phone.icon'

import { isBaseProduct } from '@utils/AccountAccess/accountProductStatuses'
import { LOCALE_MAP, getContactUsLink, isTickmillUKType } from '@utils/companyName.utils'

import { LegalDocumentDto } from '@model/CompanyLegalDocumentDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { useDispatchOpenChat } from '@hooks/useDispatchOpenChat'
import { useHomePageUrl } from '@hooks/useHomePageUrl'
import { useLinksConfig } from '@hooks/useLinksConfig'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { Box } from '@shared/components/Box'

import {
  AgreedLegalDocument,
  ReadDocumentPage,
} from '../../Signup/Components/DocumentReadAndAgreeField'
import { FormValuesLegalDocs } from '../../Signup/Pages/PersonalDetails/PersonalDetailsDocumentsFactory'
import { DocumentGroupsFactory } from '../../Signup/Pages/PersonalDetails/PersonalDetailsStep2/DocumentGroupsFactory'
import { DocumentItemFactory } from '../../Signup/Pages/PersonalDetails/PersonalDetailsStep6/DocumentItemFactory'
import { ProductRegistrationStepsTemplate } from '../ProductRegistrationStepsPage/ProductRegistrationStepsTemplate'
import { DocumentMarketDataClassifications } from './DocumentMarketDataClassifications'
import {
  ReadDocumentMarketDataPage,
  useDocumentMarketDataClassificationsMutate,
  useDocumentMarketDataIdState,
} from './ReadDocumentMarketDataPage'

import styles from './LegalDocumentsPage.module.scss'

export interface LegalDocumentsValues {
  agreedLegalDocuments: AgreedLegalDocument[]
  documents?: { [key: string]: boolean }
  readDocuments: LegalDocumentDto | undefined
  readDocumentMarketData: ReadDocumentMarketData
}

interface ReadDocumentMarketData {
  marketDataClassificationId: number
  isDocumentMarketDataRead: boolean
  isDocumentMarketDataAccepted: boolean
}

type GroupReference = string | null

const SupportOptionsExternalLinks = ({ className }: { className?: string }) => {
  const {
    links: { SupportOptionsLinks },
  } = useLinksConfig()

  const resolvedProductId = useResolvedAppTestProduct()
  const locale = useSessionLanguage()

  const localeCode = '{localeCode}'
  const localeForLinks = LOCALE_MAP[locale] ?? locale

  return (
    resolvedProductId === TickmillProductType.ETD && (
      <div className={classNames(styles.supportLinksWrapper, className)}>
        <Link
          href={SupportOptionsLinks.TICKMILL_UK.futuresAndOptions.replace(
            localeCode,
            localeForLinks
          )}
          size='lg'
        >
          {t('Support.Futures & Options')}
        </Link>

        <Link
          href={SupportOptionsLinks.TICKMILL_UK.supportedPlatformsCQG.replace(
            localeCode,
            localeForLinks
          )}
          size='lg'
        >
          {t('Support.Supported platforms via CQG')}
        </Link>

        <Box mt='x8' pt='x16' borderTop='outline-low-em'>
          <AlertNotice
            id='minimum-futures-options-account-activation'
            type='info'
            message={
              <>
                {t('Support.Minimum FUTURES & OPTIONS Account Activation')}:{' '}
                <Body1 bold>1,000 USD</Body1>
              </>
            }
          />
        </Box>
      </div>
    )
  )
}

const LegalDocumentsFormUI: React.FC<FormikProps<LegalDocumentsValues> & OuterProps> = (props) => {
  const { title, subtitle, values, handleSubmit, documents, groupReferences, validateForm } = props
  const { readDocuments, readDocumentMarketData } = values

  const homePageUrl = useHomePageUrl()

  const { t } = useTranslation()
  const entity = useSessionEntity()

  const resolvedProductId = useResolvedAppTestProduct()

  useEffect(() => {
    validateForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readDocuments])

  if (readDocuments) {
    return <ReadDocumentPage />
  }

  if (readDocumentMarketData?.isDocumentMarketDataRead) {
    return <ReadDocumentMarketDataClientClassificationPage />
  }

  return (
    <ProductRegistrationStepsTemplate title={title} subtitle={subtitle}>
      <div className={classNames(styles.wrapper, 'is-flex')}>
        <SupportOptions />
        <div className={styles.form}>
          <Form onSubmit={handleSubmit}>
            <Heading6 medium>
              {resolvedProductId === TickmillProductType.ETD
                ? t('Legal Agreements')
                : t('Legal Documents')}
            </Heading6>
            <div className='mt-5'>
              {isTickmillUKType(entity) && isBaseProduct(resolvedProductId) ? (
                [
                  <Body1>
                    {t('Sign up.Please refer to our legal documents section')}{' '}
                    <Link
                      title={
                        resolvedProductId === TickmillProductType.ETD
                          ? t('Legal Agreements')
                          : t('Legal Documents')
                      }
                      href={`${homePageUrl}/about/legal-documents`}
                      size={'lg'}
                    >
                      {t('Sign up.here')}
                    </Link>
                    {'.'}
                  </Body1>,
                  <Body1 className='is-flex py-5'>
                    {t(
                      'Sign up.Read & Accept the Legal Documents to continue with your registration.'
                    )}
                  </Body1>,
                ]
              ) : (
                <Body1 className='is-flex pb-5'>
                  {t(
                    'Sign up.Please check the boxes below once you’ve reviewed our legal documentation.'
                  )}
                </Body1>
              )}
            </div>

            <SupportOptionsExternalLinks className={styles.supportLinksContainer} />
            <DocumentGroupsFactory
              groupReferences={groupReferences}
              documents={documents}
              renderItem={(props) => <DocumentItemFactory {...props} />}
            />
            {isTickmillUKType(entity) && resolvedProductId === TickmillProductType.ETD && (
              <DocumentMarketDataClassifications />
            )}
            <SubmitForm />
          </Form>
        </div>
      </div>
    </ProductRegistrationStepsTemplate>
  )
}

const ReadDocumentMarketDataClientClassificationPage = () => {
  const { values, setFieldValue } = useFormikContext<FormValuesLegalDocs>()

  const mutation = useDocumentMarketDataClassificationsMutate()
  const { setMarketDataClassificationId } = useDocumentMarketDataIdState()

  const handleAccept = async () => {
    await mutation.mutate(values, () => {
      const value = values.readDocumentMarketData.marketDataClassificationId || 0
      setMarketDataClassificationId(value)
      setFieldValue('readDocumentMarketData', {
        marketDataClassificationId: value,
        isDocumentMarketDataRead: false,
        isDocumentMarketDataAccepted: true,
      })
    })
  }

  return <ReadDocumentMarketDataPage onAccept={handleAccept} />
}

const SubmitForm = () => {
  useScrollToTop()
  const { t } = useTranslation()
  const resolvedProductId = useResolvedAppTestProduct()

  const { isSubmitting, isValid, values } = useFormikContext<LegalDocumentsValues>()

  const isDocumentMarketDataAccepted = values.readDocumentMarketData.isDocumentMarketDataAccepted

  return (
    <div className='has-text-centered'>
      <Button.Primary
        type='submit'
        size='lg'
        disabled={
          isSubmitting ||
          !isValid ||
          (resolvedProductId === TickmillProductType.ETD && !isDocumentMarketDataAccepted)
        }
      >
        {t('Continue')}
      </Button.Primary>
    </div>
  )
}

interface OuterProps {
  title: string
  subtitle?: string | React.ReactNode
  groupReferences: GroupReference[]
  documents: LegalDocumentDto[]
  marketDataClassificationId: number

  onSubmit(v: LegalDocumentsValues): Promise<void>
}

export const LegalDocumentsForm = withFormik<OuterProps, LegalDocumentsValues>({
  mapPropsToValues: (props) => {
    return {
      agreedLegalDocuments: [],
      readDocuments: undefined,
      readDocumentMarketData: {
        marketDataClassificationId: props.marketDataClassificationId || 0,
        isDocumentMarketDataRead: false,
        isDocumentMarketDataAccepted: props.marketDataClassificationId >= 1 || false,
      },
      documents: {},
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    const { onSubmit } = props

    try {
      setSubmitting(true)
      await onSubmit(values)
      setSubmitting(false)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, { documents }) => {
    const errors: FormikErrors<LegalDocumentsValues> = {}

    if (documents.length <= 0) {
      errors.agreedLegalDocuments = t('Validation.Required')
    }

    if (!isDocumentsPositiveValue(values.documents)) {
      errors.agreedLegalDocuments = t('Validation.Required')
    }

    if (!isDocumentsAgreed(documents, values.agreedLegalDocuments)) {
      errors.agreedLegalDocuments = t('Validation.Required')
    }

    return errors
  },
  validateOnMount: true,
  enableReinitialize: false,
})(LegalDocumentsFormUI)

const isDocumentsPositiveValue = (documents: { [key: string]: boolean } = {}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return Object.entries(documents || []).every(([_, value]) => value)
}

const isDocumentsAgreed = (
  documents: LegalDocumentDto[] = [],
  agreedLegalDocuments: AgreedLegalDocument[] = []
) => {
  return documents.every((document) =>
    agreedLegalDocuments.some((agreedDocument) => agreedDocument.id === document.id)
  )
}

const SupportOptions = (): ReactElement<any> => {
  const { t } = useTranslation()
  const { domains } = usePublicDomainsReadContext()
  const { dispatchOpenChat, isLoading } = useDispatchOpenChat()
  const locale = useSessionLanguage()
  const link = getContactUsLink(domains, locale)
  const handleChatLinkClick = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault()
    if (isLoading) return

    dispatchOpenChat()
  }

  return (
    <div className={classNames(styles.supportOptions, 'is-fullwidth')}>
      <div className='mb-3'>
        <span className='has-text-weight-bold'>{t('Support.We are here to help')}</span>
      </div>

      <span className={styles.helpIcon}>
        <ChatCircleDotsIcon size={20} />
        <Link onClick={handleChatLinkClick} disabled={isLoading} size={'lg'}>
          {t(!isLoading ? 'Support.Start a Chat' : 'Loading...')}
        </Link>
      </span>
      <span className={styles.helpIcon}>
        <PhoneIcon size={20} />
        <Link href={link} size={'lg'}>
          {t('Support.Call Us')}
        </Link>
      </span>
      <span className={styles.helpIcon}>
        <MailIcon size={20} />
        <Link href={link} size={'lg'}>
          {t('Support.Send a Message')}
        </Link>
      </span>

      <SupportOptionsExternalLinks />
    </div>
  )
}
